import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ForestProfileProvider } from './context/ForestProfileContext';
import { useSeoMeta } from './lib/useSeoMeta';
import { AUTH_ENABLED } from './lib/featureFlags';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Calculator from './pages/Calculator';
import Dashboard from './pages/Dashboard';
import Sensitivity from './pages/Sensitivity';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Villkor from './pages/Villkor';
import About from './pages/About';
import Verktyg from './pages/Verktyg';
import MinSkog from './pages/MinSkog';
import Datakallor from './pages/Datakallor';
import SparadeBer from './pages/SparadeBer';
import MinaArenden from './pages/MinaArenden';
import ForgotPassword from './pages/ForgotPassword';
import Metodik from './pages/Metodik';
import AdminPage from './pages/AdminPage';
import AdminSetup from './pages/AdminSetup';

// SEO Meta Manager - updates canonical, robots, title per route
const SeoManager = () => {
  useSeoMeta();
  return null;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-700"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Wartemodus: while AUTH_ENABLED is false, do not send users into a
    // broken /login redirect loop. Send them quietly back to the home page.
    if (!AUTH_ENABLED) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
  }
  
  return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-700"></div>
      </div>
    );
  }
  
  // Don't redirect if already authenticated - let the page handle it
  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout><Home /></Layout>} />

      {/* Auth & account routes — paused: redirect quietly to home */}
      {!AUTH_ENABLED && (
        <>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<Navigate to="/" replace />} />
          <Route path="/calculator" element={<Navigate to="/verktyg" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/sensitivity" element={<Navigate to="/" replace />} />
          <Route path="/min-skog" element={<Navigate to="/" replace />} />
          <Route path="/sparade-berakningar" element={<Navigate to="/" replace />} />
          <Route path="/mina-arenden" element={<Navigate to="/" replace />} />
          <Route path="/admin" element={<Navigate to="/" replace />} />
          <Route path="/admin-setup" element={<Navigate to="/" replace />} />
        </>
      )}

      {AUTH_ENABLED && (
        <>
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Layout><Login /></Layout>
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Layout><Register /></Layout>
              </PublicRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={<ForgotPassword />} 
          />
          <Route 
            path="/reset-password" 
            element={<ForgotPassword />} 
          />
          <Route 
            path="/calculator" 
            element={
              <ProtectedRoute>
                <Layout><Calculator /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sensitivity" 
            element={
              <ProtectedRoute>
                <Layout><Sensitivity /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/min-skog" 
            element={
              <ProtectedRoute>
                <Layout><MinSkog /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sparade-berakningar" 
            element={
              <ProtectedRoute>
                <Layout><SparadeBer /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/mina-arenden" 
            element={
              <ProtectedRoute>
                <Layout><MinaArenden /></Layout>
              </ProtectedRoute>
            } 
          />
          <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
          <Route path="/admin-setup" element={<Layout><AdminSetup /></Layout>} />
        </>
      )}
      
      {/* Legal & public info pages */}
      <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
      <Route path="/datenschutz" element={<Layout><Datenschutz /></Layout>} />
      <Route path="/villkor" element={<Layout><Villkor /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/verktyg" element={<Layout><Verktyg /></Layout>} />
      <Route path="/datakallor" element={<Layout><Datakallor /></Layout>} />
      <Route path="/metodik" element={<Layout><Metodik /></Layout>} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ForestProfileProvider>
            <SeoManager />
            <AppRoutes />
          </ForestProfileProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
